import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { api, ApiError } from './api/client';
import { completeRedirect } from './auth/msal';
import { SessionProvider } from './auth/session';
import { router } from './router';
import { ToastProvider } from './ui';
import './styles/tokens.css';
import './styles/base.css';
import './styles/layout.css';
import './styles/ui.css';
import './styles/logo.css';
import './styles/visits.css';
import './styles/dashboard.css';

const LOGIN_ERROR_KEY = 'itv.loginError';

/** Finish an Entra redirect (if any) by exchanging the ID token for a portal session. */
async function bootstrap(): Promise<{ code: string; message: string } | null> {
  let idToken: string | null;
  try {
    idToken = await completeRedirect();
  } catch (err) {
    // MSAL failures (bad config, stale redirect state) must never blank the app.
    return {
      code: 'MSAL',
      message: err instanceof Error ? err.message : 'Sign-in could not start.',
    };
  }
  if (!idToken) {
    const stored = sessionStorage.getItem(LOGIN_ERROR_KEY);
    return stored ? (JSON.parse(stored) as { code: string; message: string }) : null;
  }
  try {
    await api.login(idToken);
    sessionStorage.removeItem(LOGIN_ERROR_KEY);
    return null;
  } catch (err) {
    const error =
      err instanceof ApiError
        ? { code: err.code, message: err.message }
        : { code: 'INTERNAL', message: 'Sign-in failed. Please try again.' };
    sessionStorage.setItem(LOGIN_ERROR_KEY, JSON.stringify(error));
    return error;
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');

const queryClient = new QueryClient();

void bootstrap().then((loginError) => {
  createRoot(rootEl).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <SessionProvider loginError={loginError}>
            <RouterProvider router={router} />
          </SessionProvider>
        </ToastProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
});
